import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Button, Divider, message, Spin, List } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from './config';
import jsPDF from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const { Content } = Layout;
const { Title, Text } = Typography;

const WODDetails = () => {
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  // Ottieni la data di creazione
  const creationDate = new Date();
  const formattedDate = creationDate.toLocaleDateString('it-IT').replace(/\//g, '-');


  const fetchWorkoutPlan = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/wod/${id}`);
      setWorkoutPlan(response.data);
    } catch (error) {
      message.error('Errore durante il caricamento del WOD');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkoutPlan();
  }, [fetchWorkoutPlan]);
  
  const generatePDF = async (workoutPlan) => {
      if (!workoutPlan) {
          message.error("Piano di allenamento non trovato.");
          return;
      }
  
      const doc = new jsPDF();
      const imgLogo = '/images/logo.png';
  
      // Aggiungi il logo e il titolo del WOD
      doc.addImage(imgLogo, 'PNG', 10, 5, 50, 30);
      doc.setFontSize(16);
      doc.setFont('Helvetica', 'bold');
      // Ottieni la data di creazione

      // Aggiungi il titolo del WOD con la data
      doc.text(`${workoutPlan.workoutPlanName || "Workout of the Day"} - ${formattedDate}`, 105, 20, null, null, 'center');

  
      // Impostazioni di partenza per la descrizione
      let yOffset = 50;
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(12);
  
      // Divider iniziale e titolo della descrizione
      doc.line(10, yOffset, 200, yOffset);
      yOffset += 10;
      // Aggiungi descrizione come elenco puntato
      const descriptionItems = workoutPlan.description ? workoutPlan.description.split('\n') : ["Descrizione non disponibile"];
      descriptionItems.forEach((item) => {
          if (yOffset > 270) { // Controllo per evitare di uscire dalla pagina
              doc.addPage();
              yOffset = 20;
          }
  
          // Inserisce un puntino e il testo dell'elemento
          doc.text(`• ${item}`, 20, yOffset);
          yOffset += 8; // Spaziatura per ogni elemento
      });
  
      const pdfBlob = doc.output('blob');
  
      // Aggiungi la data di creazione al nome del PDF
      const fileName = `WOD - ${formattedDate}.pdf`;
  
      // Verifica se è su iOS o Android e usa la funzione di condivisione
      if (Capacitor.isNativePlatform()) {
          const reader = new FileReader();
          reader.readAsDataURL(pdfBlob);
          reader.onloadend = async () => {
              const base64Data = reader.result.split(',')[1];
  
              try {
                  const writeFileResult = await Filesystem.writeFile({
                      path: `FitLab/${fileName}`,
                      data: base64Data,
                      directory: Directory.Documents,
                      recursive: true,
                  });
  
                  await Share.share({
                      title: 'Scarica il tuo PDF',
                      url: writeFileResult.uri,
                      dialogTitle: 'Condividi il piano di allenamento',
                  });
              } catch (e) {
                  console.error('Errore durante il salvataggio del file', e);
              }
          };
      } else {
          // Comportamento per il download su desktop
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      }
  };
  


  if (loading) {
    return <Spin style={{ display: 'block', marginTop: '2rem' }} />;
  }

  if (!workoutPlan) {
    return <Text style={{ marginTop: '2rem' }}>WOD non trovato</Text>;
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#ffffff', overflowX: 'hidden' }}>
      <Content style={{ maxWidth: '100%', margin: '0 10px', paddingBottom: '50px', paddingTop: '20px' }}>
        <Button
          type="primary"
          style={{
            marginBottom: '20px',
            backgroundColor: 'var(--primario)',
            borderColor: 'var(--primario)',
          }}
          onClick={() => navigate(-1)}
        >
          Indietro
        </Button>

        <Title level={2} style={{ color: 'var(--primario)', textAlign: 'center' }}>
          {workoutPlan.workoutPlanName}
        </Title>
        <Divider />

        <List
          size="large"
          dataSource={(workoutPlan.description || "Descrizione non disponibile").split('\n')}
          renderItem={(item) => (
            <List.Item style={{ padding: '10px 0', border: 'none', fontSize: '16px' }}>
              <Text style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: 'var(--primario)', fontSize: '18px', marginRight: '10px' }}>•</span>
                {item}
              </Text>
            </List.Item>
          )}
        />

        <Divider />

        <Button
          type="primary"
          onClick={() => generatePDF(workoutPlan)}
          style={{
            marginTop: '20px',
            width: '100%',
            backgroundColor: 'var(--primario)',
            borderColor: 'var(--primario)',
          }}
        >
          Scarica PDF
        </Button>
      </Content>
    </Layout>
  );
};

export default WODDetails;
